import {
  IconCertificate,
  IconChartDonut,
  IconCube,
  IconDoorEnter,
  IconFileDescription,
  IconFileTextSpark,
  IconHourglass,
  IconLogin2,
  IconMailForward,
  IconReceipt,
  IconSearch,
  IconServer,
  IconUsers,
  IconUserScan,
  IconUsersGroup,
  IconWallet,
  IconWebhook,
} from '@tabler/icons-react'

import type { IconComponent } from '@/lib/icons/types'

export interface ReportsMenuItem {
  id: string
  path: string
  labelKey: string
  icon: IconComponent
  hasPeriod: boolean
}

export interface ReportsMenuSection {
  sectionKey:
    | 'envelopes'
    | 'signers'
    | 'templates'
    | 'team'
    | 'consumption'
    | 'system'
  items: Array<ReportsMenuItem>
}

export const reportsMenuConfig: Array<ReportsMenuSection> = [
  {
    sectionKey: 'envelopes',
    items: [
      {
        id: 'envelopes-overview',
        path: '/reports/envelopes/overview',
        labelKey: 'menu.envelopes.overview',
        icon: IconFileDescription,
        hasPeriod: true,
      },
      {
        id: 'envelopes-pending',
        path: '/reports/envelopes/pending',
        labelKey: 'menu.envelopes.pending',
        icon: IconHourglass,
        hasPeriod: true,
      },
      {
        id: 'envelopes-sealing',
        path: '/reports/envelopes/sealing',
        labelKey: 'menu.envelopes.sealing',
        icon: IconCertificate,
        hasPeriod: true,
      },
      {
        id: 'envelopes-blockchain',
        path: '/reports/envelopes/blockchain',
        labelKey: 'menu.envelopes.blockchain',
        icon: IconCube,
        hasPeriod: true,
      },
      {
        id: 'envelopes-accesses',
        path: '/reports/envelopes/accesses',
        labelKey: 'menu.envelopes.accesses',
        icon: IconDoorEnter,
        hasPeriod: true,
      },
    ],
  },
  {
    sectionKey: 'signers',
    items: [
      {
        id: 'signers-actions',
        path: '/reports/signers/actions',
        labelKey: 'menu.signers.actions',
        icon: IconUsers,
        hasPeriod: true,
      },
      {
        id: 'signers-identity',
        path: '/reports/signers/identity',
        labelKey: 'menu.signers.identity',
        icon: IconUserScan,
        hasPeriod: true,
      },
      {
        id: 'signers-notifications',
        path: '/reports/signers/notifications',
        labelKey: 'menu.signers.notifications',
        icon: IconMailForward,
        hasPeriod: true,
      },
    ],
  },
  {
    sectionKey: 'templates',
    items: [
      {
        id: 'templates-performance',
        path: '/reports/templates/performance',
        labelKey: 'menu.templates.performance',
        icon: IconFileTextSpark,
        hasPeriod: true,
      },
    ],
  },
  {
    sectionKey: 'team',
    items: [
      {
        id: 'team-productivity',
        path: '/reports/team/productivity',
        labelKey: 'menu.team.productivity',
        icon: IconUsersGroup,
        hasPeriod: true,
      },
    ],
  },
  {
    sectionKey: 'consumption',
    items: [
      {
        id: 'consumption-statement',
        path: '/reports/consumption/statement',
        labelKey: 'menu.consumption.statement',
        icon: IconReceipt,
        hasPeriod: true,
      },
      {
        id: 'consumption-balance',
        path: '/reports/consumption/balance',
        labelKey: 'menu.consumption.balance',
        icon: IconWallet,
        hasPeriod: false,
      },
      {
        id: 'consumption-allocation',
        path: '/reports/consumption/allocation',
        labelKey: 'menu.consumption.allocation',
        icon: IconChartDonut,
        hasPeriod: true,
      },
      {
        id: 'consumption-storage',
        path: '/reports/consumption/storage',
        labelKey: 'menu.consumption.storage',
        icon: IconServer,
        hasPeriod: false,
      },
    ],
  },
  {
    sectionKey: 'system',
    items: [
      {
        id: 'system-audit',
        path: '/reports/system/audit',
        labelKey: 'menu.system.audit',
        icon: IconSearch,
        hasPeriod: true,
      },
      {
        id: 'system-access',
        path: '/reports/system/access',
        labelKey: 'menu.system.access',
        icon: IconLogin2,
        hasPeriod: true,
      },
      {
        id: 'system-webhooks',
        path: '/reports/system/webhooks',
        labelKey: 'menu.system.webhooks',
        icon: IconWebhook,
        hasPeriod: true,
      },
    ],
  },
]

export function findReportItem(pathname: string) {
  for (const section of reportsMenuConfig) {
    for (const item of section.items) {
      if (item.path === pathname) {
        return { section, item }
      }
    }
  }
  return null
}

export const defaultReportPath = reportsMenuConfig[0].items[0].path

const PERIOD_KEYS = ['preset', 'from', 'to'] as const

export function periodSearch(
  path: string,
  search: Record<string, unknown>,
): Record<string, string> {
  if (!findReportItem(path)?.item.hasPeriod) return {}
  return Object.fromEntries(
    PERIOD_KEYS.flatMap((key) => {
      const value = search[key]
      return typeof value === 'string' ? [[key, value]] : []
    }),
  )
}

export function reportSearchKey(id: string): string {
  const pascal = id
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
  return `pages.reports${pascal}`
}
